/**
 * S-3058A: Frozen V1 Overlay Theme
 * 
 * DO NOT MODIFY - V1 style reference for visual parity
 * 
 * This file contains the exact colors, line widths, alpha values, and canvas
 * context settings used by the V1 overlay (OverlayCanvas.tsx).
 * 
 * Any changes here will break visual equivalence between V1 and V2 overlays.
 * If you need to change the overlay appearance, create a new theme version
 * (e.g., overlayThemeV2.ts) and update the story docs accordingly.
 */

export interface OverlayTheme {
  colors: {
    torso: string;
    leftArm: string;
    rightArm: string;
    leftLeg: string;
    rightLeg: string;
    joint: string;
  };
  alpha: {
    stroke: number;
    joint: number;
  };
  lineWidths: {
    torso: number;
    arm: number;
    leg: number;
  };
  joint: {
    radius: number;
  };
  ctx: {
    lineCap: CanvasLineCap;
    lineJoin: CanvasLineJoin;
    miterLimit: number;
  };
}

/**
 * V1 Overlay Theme - Frozen Reference
 * 
 * These values are extracted from the V1 implementation:
 * - src/features/swing-lab/analysis/OverlayCanvas.tsx
 * - src/features/swing-lab/config/poseOverlay.config.ts
 * 
 * Colors use the V2 color scheme (from renderWorker.ts) which is:
 * - Torso: Green (rgba(34,197,94,0.8))
 * - Left Arm: Blue (rgba(59,130,246,0.7))
 * - Right Arm: Red (rgba(239,68,68,0.7))
 * - Left Leg: Purple (rgba(168,85,247,0.7))
 * - Right Leg: Orange (rgba(251,146,60,0.7))
 * - Joints: Green (rgba(34,197,94,0.9))
 */
export const OVERLAY_THEME_V1: Readonly<OverlayTheme> = Object.freeze({
  colors: Object.freeze({
    torso: 'rgba(34,197,94,0.8)',
    leftArm: 'rgba(59,130,246,0.7)',
    rightArm: 'rgba(239,68,68,0.7)',
    leftLeg: 'rgba(168,85,247,0.7)',
    rightLeg: 'rgba(251,146,60,0.7)',
    joint: 'rgba(34,197,94,0.9)',
  }),
  alpha: Object.freeze({
    stroke: 0.8,
    joint: 0.8,
  }),
  lineWidths: Object.freeze({
    torso: 2,
    arm: 1.8,
    leg: 2,
  }),
  joint: Object.freeze({
    radius: 4,
  }),
  ctx: Object.freeze({
    lineCap: 'round' as CanvasLineCap,
    lineJoin: 'round' as CanvasLineJoin,
    miterLimit: 10,
  }),
});

